import { createContext, useMemo, ReactNode } from 'react'
import { Capacitor } from '@capacitor/core'
import { useAuth } from '../hooks/useAuth'
import { usePresenceConfig, usePresenceRuntime } from '../hooks/usePresence'
import getOrCreateClientSessionId from '../utils/functions/clientSessionId'

// ── Context ───────────────────────────────────────

export interface PresenceContextValue {
  /** Whether presence tracking is active in this session */
  enabled: boolean
  /** Stable per-tab UUID that identifies this browser tab to the backend */
  clientSessionId: string
}

export const PresenceContext = createContext<PresenceContextValue | undefined>(undefined)

// ── Provider ──────────────────────────────────────

export const PresenceProvider = ({ children }: { children: ReactNode }) => {
  // Gate the config fetch on auth readiness: the Auth0 token getter is wired
  // into the axios interceptor only after isAuthenticated flips true, so firing
  // GET /user/config/ earlier sends no Authorization header and 403s.
  const { isAuthenticated } = useAuth()
  const { data: config } = usePresenceConfig(isAuthenticated)

  /*
   * Platform gate — presence is web-only for now (browser-tab active time via
   * react-idle-timer). A future phase will track mobile app-foreground time.
   * When that lands:
   *   1. Replace `!Capacitor.isNativePlatform()` with a platform-appropriate
   *      idle/foreground source (e.g. Capacitor App plugin `appStateChange`).
   *   2. Keep this provider and usePresenceRuntime as the shared entry point.
   *   3. Only the `enabled` boolean and the idle source inside usePresenceRuntime
   *      need to change — no other code should encode web-vs-native assumptions.
   */
  const enabled =
    isAuthenticated &&
    !!config?.presence_heartbeat_enabled &&
    !Capacitor.isNativePlatform()

  usePresenceRuntime(enabled)

  // Resolve the per-tab id once — getOrCreateClientSessionId touches
  // sessionStorage and must not run in the render path on every render.
  const clientSessionId = useMemo(() => getOrCreateClientSessionId(), [])

  const value = useMemo<PresenceContextValue>(
    () => ({ enabled, clientSessionId }),
    [enabled, clientSessionId],
  )

  return <PresenceContext.Provider value={value}>{children}</PresenceContext.Provider>
}
